import { useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { RichTextEditor } from '@teamsnap/snap-ui';
import {
  Button,
  Field,
  Loader,
  Modal,
  Select,
  Tag,
  ToggleCheckboxList,
} from '@teamsnap/teamsnap-ui';
import api from 'core/api';
import { TINY_MCE_API_KEY } from 'core/constants';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import { Banner } from '@teamsnap/snap-ui';
import axios from 'axios';
import { trackChurnZeroEvent } from 'components/common/useChurnZero';
import { useCallback, useEffect } from 'react';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';
import * as CreateEmail from '../../core/gql/mutations/create-email';
import * as ProgramSeasonsDivisions from '../../core/gql/queries/program-season-divisions';
import * as OrganizationQuery from '../../core/gql/queries/show-organizations';
import * as UserQuery from '../../core/gql/queries/show-user';
import { Data as UserData } from '../../core/gql/show-users';
import { messagesLink } from '../../utils/links';

type ImageUploadResponse = {
  data: {
    signedUrl: string;
  };
};

// TODO move to shared util
const NEWLINES_NOT_INSIDE_TAGS = /(?:\r\n|\r|\n)(?![^<]*>)/g; // Matches newlines not inside HTML tags
const MAX_BYTES_EMAIL_BODY_SIZE = 25000000; // 25 MB in bytes

export async function uploadImage(
  userUciUuid: string,
  orgId: string,
  blobInfo,
  _
): Promise<string> {
  const blob = blobInfo.blob();
  const fileSize = blob.size;
  if (fileSize >= MAX_BYTES_EMAIL_BODY_SIZE) {
    Sentry.captureMessage('Image size too large for RichTextEditor', {
      extra: {
        orgId,
        fileSize: `${(fileSize / 1000000).toFixed(2)} MB`,
        userUciUuid,
      },
    });
    return Promise.reject({
      message: 'Image size is too large! (25 MB max)',
      remove: true,
    });
  }
  try {
    const fileName = blob.name || 'Uploaded image';
    const response = await api.post(
      `/organizations/${orgId}/broadcast_email_generate_signed_url`,
      {
        fileName,
        contentType: blob.type,
      }
    );
    const signedUrlstring = (response as ImageUploadResponse).data.signedUrl;
    const signedUrl = new URL(signedUrlstring);
    await axios.put(signedUrlstring, blob, {
      headers: { 'Content-Type': blob.type },
    });

    return `${signedUrl.protocol}//${signedUrl.hostname}${signedUrl.pathname}`;
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        orgId,
        userUciUuid,
      },
    });
    reactiveFeedbackMessage({
      message:
        'Oops! Failed to upload image. Please try again or contact TeamSnap support for help.',
      isError: true,
      autoclose: true,
    });
  }
}

export interface Message {
  id: number;
  name: string;
  seasons?: [
    {
      id: string;
      name: string;
      tsDivisionId: string;
      isArchived: boolean;
      divisions: [
        {
          id: string;
          name: string;
        }
      ];
    }
  ];
}

interface Props {
  orgId: string;
}

interface HeaderStatus {
  [key: string]: {
    activeCount: number;
    status: boolean;
  };
}

interface validationType {
  [key: string]: {
    error: {
      state: boolean;
      message: string;
      classes?: string;
    };
  };
}

interface ExpandableList {
  heading: string;
  subheading: string;
  tags: React.ReactNode[];
  rows: string[];
}

const fields: validationType = {
  subject: {
    error: {
      state: false,
      message: 'You must enter a subject for the message.',
      classes: 'error',
    },
  },
  body: {
    error: {
      state: false,
      message: 'You must enter body text for the message.',
      classes: 'error',
    },
  },
  divisions: {
    error: {
      state: false,
      message: 'You must select a recipient for the message.',
      classes: 'error',
    },
  },
};

const NewMessage: React.FC<Props> = ({ orgId }: Props) => {
  const [selectedRecipients, setSelectedRecipients] = React.useState<string[]>(
    []
  );
  const [headerStatus, setHeaderStatus] = React.useState<HeaderStatus>({});
  const [subjectText, setSubjectText] = React.useState<string>('');
  const [senderEmailFrom, setSenderEmailFrom] = React.useState<string>('');
  const [senderName, setSenderName] = React.useState<string>('');
  const [bodyText, setBodyText] = React.useState<string>('');
  const [sending, setSending] = React.useState(false);
  const [validation, setValidation] = React.useState(fields);
  const [programDivisions, setProgramDivisions] = React.useState([]);

  const inputRef = React.useRef(null);
  const bodyRef = React.useRef(null);
  const userRef = React.useRef<UserData>(null);

  const history = useHistory();

  const userQuery = useQuery(UserQuery.QUERY);
  const { data } = useQuery(ProgramSeasonsDivisions.QUERY, {
    variables: { organizationId: parseInt(orgId) },
  });
  const [createEmail] = useMutation(CreateEmail.MUTATION);
  const organizationQuery = useQuery(OrganizationQuery.QUERY, {
    variables: { organizationId: parseInt(orgId) },
  });

  const user = userQuery.data?.user;

  useEffect(() => {
    // For some reason, the user does not get updated when passed into
    // handleImageUpload (even though it is a useCallback). This is a workaround
    userRef.current = user;
  }, [user]);

  React.useEffect(() => {
    if (data) {
      const formattedData = prepData(data);
      setProgramDivisions(formattedData);
    }
  }, [data]);

  React.useEffect(() => {
    if (user) {
      setSenderEmailFrom(user.email);
      setSenderName(`${user.firstName} ${user.lastName}`);
    } else {
      setSenderEmailFrom(doNotReplyEmail);
      setSenderName(organization?.name || 'Unknown organization name');
    }
  }, [user]);

  React.useEffect(() => {
    const state = {
      ...validation,
    };

    if (
      selectedRecipients.length > 0 ||
      (Object.keys(headerStatus).length > 0 && state.divisions.error.state)
    ) {
      state.divisions.error.state = false;
    }

    setValidation(state);
  }, [selectedRecipients]);

  const organization = organizationQuery.data?.showOrganization;

  const doNotReplyEmail = 'donotreply@email.teamsnap.com';

  const unAssignedText = 'Unassigned (not in a division)';

  const getDivisions = (divisionNames: string[]) => {
    const { programs } = data;
    let divisionsList = [];

    programs.forEach((program) => {
      const { name: programName, seasons, id: programId } = program;

      seasons.forEach((season) => {
        const {
          name: seasonName,
          id: seasonId,
          divisions,
          tsDivisionId,
        } = season;
        const programSeasonIdentifier = `${programName}-${seasonName}`;

        const hasUnassigned = divisionNames.includes(
          `${programSeasonIdentifier}-${unAssignedText}`
        );

        if (
          (divisions.length === 0 &&
            headerStatus.hasOwnProperty(programSeasonIdentifier)) ||
          hasUnassigned
        ) {
          divisionsList = [
            ...divisionsList,
            {
              id: parseInt(tsDivisionId),
              name: hasUnassigned ? 'Unassigned' : seasonName,
              programName: programName,
              programId: programId,
              seasonName: seasonName,
              seasonId: seasonId,
              isUnassigned: hasUnassigned ? true : false,
            },
          ];
        }

        divisions.forEach((division) => {
          const { id: divisionId, name: divisionName } = division;
          const programUniqueId = `${programName}-${seasonName}-${divisionName}`;

          if (divisionNames.includes(programUniqueId)) {
            divisionsList = [
              ...divisionsList,
              {
                id: divisionId,
                name: divisionName,
                programName: programName,
                programId: programId,
                seasonName: seasonName,
                seasonId: seasonId,
                isUnassigned: false,
              },
            ];
          }
        });
      });
    });

    // Using set removes duplicates and then we convert it back to an array
    return Array.from(new Set(divisionsList));
  };

  const handleSubjectChange = (text: string) => {
    const state = {
      ...validation,
    };

    if (text?.length > 0) {
      state.subject.error.state = false;
    }

    setValidation(state);
    setSubjectText(text);
  };

  const handleSenderInfo = (value) => {
    setSenderEmailFrom(value);
    if (value === doNotReplyEmail) {
      setSenderName(organization?.name || 'Unknown organization name');
    } else {
      setSenderName(`${user.firstName} ${user.lastName}`);
    }
  };

  const validate = () => {
    let validationState = true;
    const state = {
      ...validation,
    };

    if (subjectText?.length === 0) {
      state.subject.error.state = true;
      inputRef.current?.focus();
      setValidation(state);

      validationState = false;
    }

    if (bodyText?.length === 0) {
      state.body.error.state = true;
      bodyRef.current?.editor?.focus();
      bodyRef.current?.editor?.editorContainer.classList.add('error');

      setValidation(state);

      validationState = false;
    }

    // We only issue an error for recipients when we have no selected divisions and we have no header selected
    if (selectedRecipients.length === 0) {
      if (Object.keys(headerStatus).length === 0) {
        state.divisions.error.state = true;
        validationState = false;
      } else {
        state.divisions.error.state = false;
      }

      setValidation(state);
    }

    return validationState;
  };

  const send = async () => {
    if (validate()) {
      setSending(true);
      const divisions = getDivisions(selectedRecipients);

      try {
        const response = await createEmail({
          variables: {
            organizationId: parseInt(orgId),
            subject: subjectText,
            body: bodyText,
            divisions: divisions,
            isDraft: false,
            isOrg: true,
            senderEmailAddress: senderEmailFrom,
            senderName: senderName,
          },
        });

        setSending(false);

        if (!sending) {
          if (response?.data?.createEmail?.id) {
            window.location.href = messagesLink(orgId);

            reactiveFeedbackMessage({
              message: 'Your message has been sent',
              isError: false,
              autoclose: true,
            });
            trackChurnZeroEvent(
              'Gemini - Org Level Message Sent',
              organization
            );
          } else {
            reactiveFeedbackMessage({
              message:
                'Oops!  We dropped the ball. Please send your message again.',
              isError: true,
              autoclose: true,
            });

            console.error('Unable to send message to recipients.');
          }
        }
      } catch (error) {
        setSending(false);

        // We should log this in Sentry, this is likely an API/ Developer error
        console.error('Unable to send message to recipients.');
        if (error.graphQLErrors) {
          const [errors] = error.graphQLErrors;
          reactiveFeedbackMessage({
            message: errors.message,
            isError: true,
            autoclose: true,
          });
        } else {
          reactiveFeedbackMessage({
            message:
              'Oops!  We dropped the ball. Please send your message again.',
            isError: true,
            autoclose: true,
          });
        }
      }
    }
  };

  const onBodyChange = (event) => {
    const content = event.target.getContent();
    if (content.length > 0) {
      const state = {
        ...validation,
      };

      state.body.error.state = false;

      setValidation(state);
      bodyRef.current.editor.editorContainer.classList.remove('error');
    }

    setBodyText(content.replace(NEWLINES_NOT_INSIDE_TAGS, ''));
  };

  const sortList = (list) => {
    const newList = [];
    // Sort subheaders
    list.forEach(({ __typename, id, name, seasons }) => {
      newList.push({
        __typename,
        id,
        name,
        seasons: seasons.slice().sort((a, b) => {
          const aString = a.name.toLowerCase();
          const bString = b.name.toLowerCase();

          if (aString === bString) {
            return 0;
          } else {
            return aString < bString ? -1 : 1;
          }
        }),
      });
    });

    // sort headers
    return newList.sort((a: any, b: any) => {
      const aString = a.name.toLowerCase();
      const bString = b.name.toLowerCase();

      if (aString === bString) {
        return 0;
      } else {
        return aString < bString ? -1 : 1;
      }
    });
  };

  const renderTags = ({ isArchived, name, tsDivisionId }) => {
    if (isArchived) {
      return [
        <Tag key={tsDivisionId} text={name} mods="u-spaceLeftSm" />,
        <Tag
          key={tsDivisionId}
          icon="archive"
          text="Archived"
          mods="u-spaceLeftSm"
        />,
      ];
    }

    return [<Tag key={tsDivisionId} text={name} mods="u-spaceLeftSm" />];
  };

  const prepData = ({ programs }) => {
    const newData: ExpandableList[] = [];
    const list = sortList([...programs]);

    list.forEach((program: Message) => {
      program.seasons?.forEach((season) => {
        const rows = season.divisions.map((division) => division.name);
        // Sort divisions
        rows.sort((a, b) => (a[0] > b[0] ? 1 : 0));
        // Add unassigned to all seasons
        if (rows.length > 0) {
          rows.unshift(unAssignedText);
        }

        newData.push({
          heading: program.name,
          subheading: season.name,
          tags: renderTags(season),
          rows: rows,
        });
      });
    });

    return newData;
  };

  const { emailSizeInBytes, emailSizeInMB } = React.useMemo(() => {
    const emailSizeInBytes = new Blob([subjectText, bodyText]).size;
    return {
      emailSizeInBytes,
      emailSizeInMB: emailSizeInBytes / 1024 / 1024,
    };
  }, [subjectText, bodyText]);

  const handleImageUpload = useCallback(
    (blobInfo, progress) =>
      uploadImage(userRef.current.uciUuid, orgId, blobInfo, progress),
    [orgId]
  );

  return (
    <>
      <div className="page-content-body u-xs-size6of6">
        <div className="Grid-cell u-xs-padTopSm u-xs-padBottomXl">
          <h4>Reply To</h4>
          <Select
            name="Reply To"
            options={[
              {
                label: `${user?.firstName} ${user?.lastName} (${user?.email})`,
                value: user?.email,
              },
              {
                label: 'Do not allow replies to this message',
                value: doNotReplyEmail,
              },
            ]}
            inputProps={{
              onChange: (e) => handleSenderInfo(e.target.value),
              defaultValue: `${user?.email || doNotReplyEmail}`,
            }}
          />
        </div>

        <div className="Grid-cell u-xs-padTopSm u-xs-padBottomXl">
          <Field
            type="input"
            formFieldProps={{
              placeholder: 'Subject',
              size: 'large',
              inputProps: {
                value: subjectText,
                onChange: (e) => handleSubjectChange(e.target.value),
                maxLength: 255,
                className: `${
                  validation.subject.error.state
                    ? 'Input Input--large error'
                    : 'Input Input--large'
                }`,
                ref: inputRef,
              },
            }}
            name="Subject"
          />
          {validation.subject.error.state && (
            <div className="Grid-cell u-xs-padEndsXs validation-error">
              {validation.subject.error.message}
            </div>
          )}
        </div>
        <div className="Grid-cell u-xs-padBottomXl tiny-mce-custom-style-for-emails">
          <RichTextEditor
            ref={bodyRef}
            height={'850'}
            tinymceApiKey={TINY_MCE_API_KEY}
            onChange={onBodyChange}
            imagesUploadHandler={handleImageUpload}
          />
          {validation.body.error.state && (
            <div className="Grid-cell u-xs-padEndsXs validation-error">
              {validation.body.error.message}
            </div>
          )}
        </div>
        {emailSizeInBytes > MAX_BYTES_EMAIL_BODY_SIZE && (
          <Banner
            sentiment="negative"
            title="Embedded content is too large "
            caption={`Limit files and images to 25MB or smaller. Current email size is: ${emailSizeInMB.toFixed(
              2
            )}MB`}
          />
        )}

        <div className="Grid-cell u-xs-padBottomXl">
          <ToggleCheckboxList
            className={validation.divisions.error.state ? 'error' : ''}
            label="Recipients"
            list={programDivisions}
            activeRows={selectedRecipients}
            headerStatus={headerStatus}
            setActiveRows={setSelectedRecipients}
            setHeaderStatus={setHeaderStatus}
          />
          {validation.divisions.error.state && (
            <div className="Grid-cell u-xs-padEndsXs validation-error">
              {validation.divisions.error.message}
            </div>
          )}
        </div>

        <div className="Grid-cell u-xs-padBottomXl">
          <Button
            mods="u-spaceRightMd u-padSidesLg"
            onClick={() => history.push(messagesLink(orgId))}
          >
            Cancel
          </Button>
          <Button
            mods="u-padSidesXl"
            color="primary"
            onClick={send}
            isDisabled={
              Object.keys(headerStatus).length === 0 ||
              sending ||
              emailSizeInBytes > MAX_BYTES_EMAIL_BODY_SIZE
            }
          >
            Send
          </Button>
        </div>
      </div>

      <Modal
        heading={<div className="u-textCenter">Sending</div>}
        show={sending}
        closeFn={() => setSending(false)}
      >
        <div className="u-flex u-flexJustifyCenter u-padBottomMd">
          <Loader type="pulse" />
        </div>
      </Modal>
    </>
  );
};

export default NewMessage;
